import { createSectionData } from '@/utils/order-helpers';
import { gql, NetworkStatus } from '@apollo/client';
import {
  ORDER_INVOICE_CHECK_IN_FIELDS_FRAGMENT,
  ORDER_INVOICE_LINE_ITEM_FRAGMENT,
} from '@/components/orders/hooks/use-get-order';
import {
  OrderHistory_InvoicesQuery,
  OrderHistory_InvoicesQueryVariables,
} from '@/types';
import { useEffect, useMemo, useState } from 'react';
import { useToasts } from '@/contexts/toast-context';
import { useUserContext } from '@/contexts/user-context';
import { useWrappedQuery } from './use-wrapped-query';

const PAGE_LIMIT = 20;

export const INVOICES_WITHOUT_ORDERS_QUERY = gql`
  fragment OrderInvoiceFields on order_invoices {
    invoice_date
    invoice_number
    order_invoice_check_in {
      ...OrderInvoiceCheckInFields
    }
    order_invoice_line_items {
      ...OrderInvoiceLineItemFields
    }
    seen
    uuid
  }

  query OrderHistory_Invoices(
    $chatUUID: uuid!
    $limit: Int
    $offset: Int
    $skipCount: Boolean!
    $supplierUUID: uuid!
  ) {
    order_invoices_aggregate(
      where: {
        invoice_source: { _eq: "INGESTION" }
        chat_uuid: { _eq: $chatUUID }
        supplier_uuid: { _eq: $supplierUUID }
        # Exclude invoices with an associated order,
        # those will display in the Pepper order history
        order_uuid: { _eq: null }
        _not: { order_invoices_orders: {} }
      }
    ) @skip(if: $skipCount) {
      aggregate {
        count
      }
    }

    order_invoices(
      limit: $limit
      offset: $offset
      order_by: [{ invoice_date: desc }]
      where: {
        invoice_source: { _eq: "INGESTION" }
        chat_uuid: { _eq: $chatUUID }
        supplier_uuid: { _eq: $supplierUUID }
        # Exclude invoices with an associated order,
        # those will display in the Pepper order history
        order_uuid: { _eq: null }
        _not: { order_invoices_orders: {} }
      }
    ) {
      ...OrderInvoiceFields
    }
  }

  ${ORDER_INVOICE_CHECK_IN_FIELDS_FRAGMENT}
  ${ORDER_INVOICE_LINE_ITEM_FRAGMENT}
`;

export const useGetInvoicesWithoutOrders = (skip = false) => {
  const { selectedRestaurantUUID, selectedRestaurantRelationship } =
    useUserContext();
  const [isManualRefresh, setIsManualRefresh] = useState(false);
  const { showErrorToast } = useToasts();

  const {
    data,
    fetchMore,
    loading,
    hasResponse,
    networkStatus,
    refetch,
    setHasResponse,
  } = useWrappedQuery<
    OrderHistory_InvoicesQuery,
    OrderHistory_InvoicesQueryVariables
  >(INVOICES_WITHOUT_ORDERS_QUERY, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip,
    variables: {
      chatUUID: selectedRestaurantRelationship.chatUUIDs[0],
      limit: PAGE_LIMIT,
      skipCount: false,
      supplierUUID: selectedRestaurantRelationship.supplierUUID,
    },
  });

  useEffect(() => {
    // Reset hasResponse state when selectedRestaurantUUID changes
    setHasResponse(false);
  }, [selectedRestaurantUUID, setHasResponse]);

  const fetchMoreOffset = useMemo(() => {
    const currentCount = data?.order_invoices.length ?? 0;
    const totalCount = data?.order_invoices_aggregate?.aggregate?.count ?? 0;
    return currentCount < totalCount ? currentCount : null;
  }, [data]);

  return useMemo(() => {
    const invoices = data?.order_invoices || [];
    return {
      fetchingMore: networkStatus === NetworkStatus.fetchMore,
      fetchMore: async () => {
        if (typeof fetchMoreOffset === 'number') {
          await fetchMore<
            OrderHistory_InvoicesQuery,
            OrderHistory_InvoicesQueryVariables
          >({
            updateQuery: (prevResult, { fetchMoreResult }) => {
              return fetchMoreResult
                ? {
                    ...prevResult,
                    order_invoices: [
                      ...prevResult.order_invoices,
                      ...fetchMoreResult.order_invoices,
                    ],
                  }
                : prevResult;
            },
            variables: {
              chatUUID: selectedRestaurantRelationship.chatUUIDs[0],
              limit: PAGE_LIMIT,
              offset: fetchMoreOffset,
              skipCount: true,
              supplierUUID: selectedRestaurantRelationship.supplierUUID,
            },
          });
        }
      },
      hasResponse,
      loading,
      pastInvoices: createSectionData(invoices, true),
      refresh: async (manual = false) => {
        try {
          setIsManualRefresh(manual);
          await refetch();
        } catch {
          if (manual) {
            showErrorToast();
          }
        }
        setIsManualRefresh(false);
      },
      refreshing: isManualRefresh && networkStatus === NetworkStatus.refetch,
      upcomingInvoices: createSectionData(invoices, false),
    };
  }, [
    data?.order_invoices,
    networkStatus,
    hasResponse,
    loading,
    isManualRefresh,
    fetchMoreOffset,
    fetchMore,
    selectedRestaurantRelationship.chatUUIDs,
    selectedRestaurantRelationship.supplierUUID,
    refetch,
    showErrorToast,
  ]);
};
